// Shared building blocks for the Harmony Timesheet screens. Everything here
// is presentational: no data fetching, no app state. Colours come from the
// semantic tokens in index.css (success / warning / danger / info) so a
// palette change happens in one place, and every status shows an icon and a
// text label as well as a colour.
import React from 'react';
import {
  AlertOctagon, AlertTriangle, CheckCircle2, CircleDashed, Clock3, Inbox, Info,
  Loader2, RefreshCw, RotateCcw, Undo2, XCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { badgeVariants } from '../ui/badge';
import { Button } from '../ui/button';

// ---------------------------------------------------------------- status

const STATUS_META = {
  approved: { label: 'Approved', variant: 'success', icon: CheckCircle2 },
  submitted: { label: 'Submitted', variant: 'warning', icon: Clock3 },
  pending: { label: 'Pending', variant: 'warning', icon: Clock3 },
  rejected: { label: 'Rejected', variant: 'danger', icon: XCircle },
  reopened: { label: 'Reopened', variant: 'info', icon: RotateCcw },
  withdrawn: { label: 'Withdrawn', variant: 'neutral', icon: Undo2 },
  expired: { label: 'Expired', variant: 'neutral', icon: CircleDashed },
  draft: { label: 'Draft', variant: 'neutral', icon: CircleDashed },
};
const NOT_SUBMITTED = { label: 'Not submitted', variant: 'neutral', icon: CircleDashed };

const titleCase = (s) => String(s).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export const statusMeta = (status) => (
  STATUS_META[status] || (status ? { ...NOT_SUBMITTED, label: titleCase(status) } : NOT_SUBMITTED)
);

// Submission / request status pill: icon + label + tint, never colour alone.
// Renders a <span> so it is valid inside buttons. `compact` shows only the
// icon below the xl breakpoint (label kept for screen readers) for tight
// spots like the week strip.
export const StatusBadge = ({ status, label, size = 'md', compact = false, className, ...rest }) => {
  const meta = statusMeta(status);
  const Icon = meta.icon;
  const text = label || meta.label;
  return (
    <span
      title={text}
      className={cn(
        badgeVariants({ variant: meta.variant }),
        'max-w-full gap-1 whitespace-nowrap font-medium',
        size === 'sm' ? 'px-1.5 py-0' : 'px-2 py-0.5',
        className,
      )}
      {...rest}
    >
      <Icon aria-hidden="true" className={cn('shrink-0', size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
      <span className={cn('min-w-0 truncate', compact && 'sr-only xl:not-sr-only')}>{text}</span>
    </span>
  );
};

// Generic tinted pill for counts and labels that are not a status.
export const Pill = ({ tone = 'neutral', className, children, ...rest }) => (
  <span
    className={cn(badgeVariants({ variant: tone }), 'gap-1 whitespace-nowrap px-2 py-0.5 font-medium', className)}
    {...rest}
  >
    {children}
  </span>
);

// ---------------------------------------------------------------- layout

// Page title block. Top-level screens use the default h1 (the shell itself
// has no heading); sub-sections inside the Admin / Runn hubs pass
// as="h2" size="md" because the hub already renders the h1.
export const PageHeader = ({ title, description, actions, className, as: Heading = 'h1', size = 'lg', children, ...rest }) => (
  <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between', className)} {...rest}>
    <div className="min-w-0">
      <Heading
        className={cn(
          'font-heading font-semibold tracking-tight text-foreground',
          size === 'md' ? 'text-lg' : 'text-xl sm:text-2xl',
        )}
      >
        {title}
      </Heading>
      {description && <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p>}
      {children}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

// Sub-section navigation for the Admin and Runn hubs. These are separate,
// deep-linkable views, so they are a <nav> with aria-current rather than a
// tablist.
export const SubNav = ({ items, value, onChange, label, className, ...rest }) => (
  <nav aria-label={label} className={cn('overflow-x-auto', className)} {...rest}>
    <ul className="flex min-w-max gap-1 border-b border-border">
      {items.map((it) => {
        const active = it.value === value;
        const Icon = it.icon;
        return (
          <li key={it.value}>
            <button
              type="button"
              onClick={() => onChange(it.value)}
              aria-current={active ? 'page' : undefined}
              data-testid={it.testId}
              className={cn(
                '-mb-px inline-flex h-10 items-center gap-2 rounded-t-md border-b-2 px-3 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
              )}
            >
              {Icon && <Icon aria-hidden="true" className="h-4 w-4" />}
              {it.label}
            </button>
          </li>
        );
      })}
    </ul>
  </nav>
);

// ---------------------------------------------------------------- callouts

const CALLOUT = {
  info: { box: 'border-info-border bg-info-soft', title: 'text-info-strong', icon: 'text-info', Icon: Info },
  success: { box: 'border-success-border bg-success-soft', title: 'text-success-strong', icon: 'text-success', Icon: CheckCircle2 },
  warning: { box: 'border-warning-border bg-warning-soft', title: 'text-warning-strong', icon: 'text-warning', Icon: AlertTriangle },
  danger: { box: 'border-danger-border bg-danger-soft', title: 'text-danger-strong', icon: 'text-danger', Icon: AlertOctagon },
  neutral: { box: 'border-border bg-muted/60', title: 'text-foreground', icon: 'text-muted-foreground', Icon: Info },
};

// One banner style for every informational / warning message. `icon={null}`
// hides the icon; `actions` render under the body so they wrap on mobile.
export const Callout = ({ variant = 'info', icon, title, children, actions, className, ...rest }) => {
  const v = CALLOUT[variant] || CALLOUT.info;
  const Icon = icon === null ? null : (icon || v.Icon);
  return (
    <div className={cn('flex gap-3 rounded-lg border px-4 py-3 text-sm', v.box, className)} {...rest}>
      {Icon && <Icon aria-hidden="true" className={cn('mt-0.5 h-4 w-4 shrink-0', v.icon)} />}
      <div className="min-w-0 flex-1">
        {title && <p className={cn('font-medium', v.title)}>{title}</p>}
        {children && <div className={cn('text-foreground/80', title && 'mt-0.5')}>{children}</div>}
        {actions && <div className="mt-2.5 flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- states

export const EmptyState = ({ icon: Icon = Inbox, title, description, action, compact = false, className, ...rest }) => (
  <div
    className={cn('flex flex-col items-center justify-center px-4 text-center', compact ? 'py-6' : 'py-12', className)}
    {...rest}
  >
    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
      <Icon aria-hidden="true" className="h-5 w-5 text-muted-foreground" />
    </div>
    {title && <p className="text-sm font-medium text-foreground">{title}</p>}
    {description && <div className="mt-1 max-w-md text-sm text-muted-foreground">{description}</div>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const LoadingState = ({ label = 'Loading…', compact = false, className, ...rest }) => (
  <div
    role="status"
    aria-live="polite"
    className={cn('flex items-center justify-center gap-2 text-sm text-muted-foreground', compact ? 'py-6' : 'py-12', className)}
    {...rest}
  >
    <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
    <span>{label}</span>
  </div>
);

export const ErrorState = ({ title = 'Something went wrong', message, onRetry, compact = false, className, ...rest }) => (
  <div
    role="alert"
    className={cn('flex flex-col items-center justify-center px-4 text-center', compact ? 'py-6' : 'py-12', className)}
    {...rest}
  >
    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-danger-soft">
      <AlertTriangle aria-hidden="true" className="h-5 w-5 text-danger" />
    </div>
    <p className="text-sm font-medium text-foreground">{title}</p>
    {message && <p className="mt-1 max-w-md text-sm text-muted-foreground">{message}</p>}
    {onRetry && (
      <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
        <RefreshCw aria-hidden="true" />Try again
      </Button>
    )}
  </div>
);

// ---------------------------------------------------------------- tables
// Thin wrappers over native table elements: every prop (onClick, colSpan,
// data-testid, style ...) passes straight through. `numeric` right-aligns
// and uses tabular figures so columns of hours line up.

export const DataTable = React.forwardRef(({ className, wrapperClassName, ...rest }, ref) => (
  <div className={cn('relative w-full overflow-x-auto', wrapperClassName)}>
    <table ref={ref} className={cn('w-full border-collapse text-sm', className)} {...rest} />
  </div>
));
DataTable.displayName = 'DataTable';

export const THead = ({ className, sticky = false, ...rest }) => (
  <thead className={cn(sticky ? 'sticky top-0 z-10 bg-muted' : 'bg-muted/60', className)} {...rest} />
);

export const Th = ({ className, align, numeric, scope = 'col', ...rest }) => (
  <th
    scope={scope}
    className={cn(
      'h-10 whitespace-nowrap px-3 text-left align-middle text-xs font-medium uppercase tracking-wide text-muted-foreground',
      (numeric || align === 'right') && 'text-right',
      align === 'center' && 'text-center',
      className,
    )}
    {...rest}
  />
);

export const Td = ({ className, align, numeric, ...rest }) => (
  <td
    className={cn(
      'px-3 py-2.5 align-middle',
      (numeric || align === 'right') && 'text-right tabular-nums',
      align === 'center' && 'text-center',
      className,
    )}
    {...rest}
  />
);

export const Tr = ({ className, interactive = false, ...rest }) => (
  <tr
    className={cn(
      'border-b border-border transition-colors last:border-0',
      interactive ? 'cursor-pointer hover:bg-muted/60' : 'hover:bg-muted/30',
      className,
    )}
    {...rest}
  />
);

// ---------------------------------------------------------------- misc

const TONE_ICON = {
  primary: 'bg-primary/10 text-primary',
  success: 'bg-success-soft text-success-strong',
  warning: 'bg-warning-soft text-warning-strong',
  danger: 'bg-danger-soft text-danger-strong',
  info: 'bg-info-soft text-info-strong',
  neutral: 'bg-muted text-muted-foreground',
};

// KPI tile: label, big tabular value, optional hint. The tone only tints the
// icon chip, so a row of tiles stays calm.
export const StatCard = ({ label, value, hint, icon: Icon, tone = 'primary', className, ...rest }) => (
  <div className={cn('rounded-xl border bg-card p-4 shadow-sm', className)} {...rest}>
    <div className="flex items-start justify-between gap-3">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      {Icon && (
        <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', TONE_ICON[tone] || TONE_ICON.primary)}>
          <Icon aria-hidden="true" className="h-4 w-4" />
        </span>
      )}
    </div>
    <p className="mt-1 font-heading text-2xl font-semibold tabular-nums text-foreground">{value}</p>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const TONE_BAR = {
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  neutral: 'bg-muted-foreground/40',
};

export const ProgressBar = ({ value = 0, max = 100, tone = 'primary', label, className }) => {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.round(Math.min(value, max))}
      aria-label={label}
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}
    >
      <div
        className={cn('h-full rounded-full transition-[width] duration-300', TONE_BAR[tone] || TONE_BAR.primary)}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

// Keyboard shortcut hint.
export const Kbd = ({ className, ...rest }) => (
  <kbd
    className={cn(
      'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded border border-border bg-muted px-1 font-sans text-xs font-medium text-muted-foreground',
      className,
    )}
    {...rest}
  />
);
